import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import cx from 'clsx';
import { match, P } from 'ts-pattern';

import CompliancePlip from '../../../../components/CompliancePlip';
import { standardDateFormat } from '../../../../helpers/date';
import { selectCurrentCustomerNumber } from '../../../../redux/customer';
import { useAppDispatch, type RootState } from '../../../../redux/store';
import { ComplianceStatus } from '../../../../types';

import StatusDropdown from '../StatusDropdown';
import { ProgramComponentTypeName, TrainingChecklistTask, TrainingChecklistType } from '../types';

import {
  selectChecklistSectionComplianceById,
  selectCustomerChecklistTaskById,
  selectCustomerChecklistTaskComplianceById,
  setCustomerNotesForTask,
  updateTaskMetadata
} from '../slice';

import styles from './Checklist.module.css';
import ReferenceModal from './ReferenceModal';
import NotesModal from './NotesModal';

const TaskRow = ({
  name,
  description,
  id,
  references,
  products,
  showNotes,
  notes,
  customer_notes,
  showProducts,
  setCustomerNotes,
  checklistType
}: TrainingChecklistTask & {
  showNotes: boolean;
  showProducts: boolean;
  setCustomerNotes?: (newNotes: string) => void;
  checklistType: TrainingChecklistType;
}) => {
  const dispatch = useAppDispatch();
  const customerNumber = useSelector(selectCurrentCustomerNumber);

  const {
    componentTypeName,
    year
  }: {
    componentTypeName?: ProgramComponentTypeName;
    year?: string;
  } = useParams();

  const metadata = useSelector((state: RootState) => selectCustomerChecklistTaskById(state, id));
  let user;
  let completedDate;

  if (metadata) {
    completedDate = metadata?.completed_at;
    if (metadata?.user?.firstname) {
      user = `${metadata.user.firstname} ${metadata.user.lastname}`;
    } else if (metadata?.user?.email) {
      user = metadata.user.email;
    }
  }

  const complianceStatus = useSelector((state: RootState) => selectCustomerChecklistTaskComplianceById(state, id));
  
  const customerNotesFromMetadata = metadata?.customer_notes;

  const saveStatus = async (status: number) => {
    if (status === complianceStatus) {
      return;
    }

    return dispatch(
      updateTaskMetadata({
        customerNumber,
        status,
        id,
        meta: {
          year,
          componentTypeName,
          checklistType
        }
      })
    );
  };

  const displayNotes = customerNotesFromMetadata?.trim() || customer_notes?.trim() || notes?.trim();

  return (
    <TableRow>
      <TableCell sx={{ padding: 0 }}>
        <StatusDropdown onChange={saveStatus} value={complianceStatus} />
      </TableCell>
      <TableCell className={styles.cellWrap}>
        <div className={cx(styles.taskName, styles.whitespace)}>{name}</div>
        <p className={styles.whitespace}>{description}</p>
        <div>
          {completedDate && complianceStatus === ComplianceStatus.Complete && (
            <Box sx={{ fontStyle: 'italic' }}>
              Completed by {user} {standardDateFormat(completedDate)}
            </Box>
          )}
        </div>
      </TableCell>
      <TableCell align="center">{references && <ReferenceModal references={references} />}</TableCell>
      {showNotes && <TableCell className={styles.cellWrap}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="0.5em">
          <p className={styles.whitespace}>{displayNotes}</p>
          {setCustomerNotes && <NotesModal notes={customerNotesFromMetadata || customer_notes || notes} setNotes={setCustomerNotes} />}
        </Box>
      </TableCell>}
      {showProducts && (
        <TableCell className={styles.cellWrap}>
          {products?.map((p) => (
            <div key={p.id}>
              {p.link ? (
                <a href={p.link} className={styles.link} target="_blank">
                  {p.name}
                </a>
              ) : (
                p.name
              )}
            </div>
          ))}
        </TableCell>
      )}
    </TableRow>
  );
};

export const Section = ({
  id,
  name,
  tasks,
  checklistType
}: {
  id: number;
  name: string;
  tasks: TrainingChecklistTask[];
  checklistType: TrainingChecklistType;
}) => {
  const status = useSelector((state: RootState) => selectChecklistSectionComplianceById(state, id));
  const customerNumber = useSelector(selectCurrentCustomerNumber);

  const dispatch = useAppDispatch();
  const setCustomerNotes = (taskId: number, newNotes: string) => dispatch(setCustomerNotesForTask({ customerNumber, taskId, newNotes }));

  const showNotes = checklistType === 'security risk assessment';
  const showProducts = checklistType !== 'security risk assessment';

  return (
    tasks.length > 0 && (
      <Accordion disableGutters data-testid="checklist-section">
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <div className="flex flex-align-center">
            <CompliancePlip className="m-r-8" status={status} />
            <Typography>{name}</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <Table className={styles.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={'5%'}>Status</TableCell>
                <TableCell width={'50%'}>
                  {match(checklistType)
                    .with('checklist', () => 'Description')
                    .with(P.union('facility report', 'security risk assessment'), () => 'Question/Details')
                    .exhaustive()}
                </TableCell>
                <TableCell align="center">Reference</TableCell>
                {showNotes && <TableCell>Notes</TableCell>}
                {showProducts && <TableCell>Products</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => (
                <TaskRow
                  key={t.id}
                  {...t}
                  showNotes={showNotes}
                  showProducts={showProducts}
                  setCustomerNotes={(newNotes) => setCustomerNotes(t.id, newNotes)}
                  checklistType={checklistType}
                />
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    )
  );
};
